import { useQuery } from '@tanstack/react-query';
import type { MediaGalleryItem } from '@xxtract/ui';
import { getGtinMedia, isDocumentTypeInfo, mintGtinGalleryDescriptor, type GtinMediaParams } from '../media.api';

interface GtinGalleryQueryData {
  items: MediaGalleryItem[];
  degraded: boolean;
}

interface GtinGalleryResult extends GtinGalleryQueryData {
  isLoading: boolean;
  refetch: () => void;
}

/**
 * h3-4: GTIN-detail gallery integration. Builds on the h2-3 discovery query but resolves each
 * image item's thumbnail/web routes to real, browser-usable signed URLs (an `<img src>` can't
 * carry a Bearer header) instead of the raw BFF redirect routes {@link toGalleryItems}
 * produces. A single item's descriptor failure degrades only that item (thumbnailRoute/
 * imageRoute stay undefined -> the gallery's own placeholder renders), never the whole page.
 */
export function useGtinGalleryItems(params: GtinMediaParams, enabled = true): GtinGalleryResult {
  const { gln, gtin, targetMarket = '528', typeInfo } = params;
  const query = useQuery({
    queryKey: ['media', 'gtinGallery', gln, gtin, targetMarket, typeInfo ?? null],
    queryFn: async (): Promise<GtinGalleryQueryData> => {
      const discovery = await getGtinMedia(params);
      if (discovery.data == null) return { items: [], degraded: true };

      const items = await Promise.all(discovery.data.map(async (item): Promise<MediaGalleryItem> => {
        const itemTypeInfo = item.typeInfo ?? 'UNKNOWN';
        const fileName = item.fileName ?? item.id;
        if (isDocumentTypeInfo(itemTypeInfo)) {
          return { mediaId: item.id, typeInfo: itemTypeInfo, kind: 'document', fileName };
        }
        const [thumbnailRoute, imageRoute] = await Promise.all([
          mintGtinGalleryDescriptor(params, item.id, 'thumbnail').catch(() => undefined),
          mintGtinGalleryDescriptor(params, item.id, 'web').catch(() => undefined),
        ]);
        return { mediaId: item.id, typeInfo: itemTypeInfo, kind: 'image', fileName, ...(thumbnailRoute == null ? {} : { thumbnailRoute }), ...(imageRoute == null ? {} : { imageRoute }) };
      }));

      return { items, degraded: false };
    },
    enabled: enabled && !!gln && !!gtin,
    staleTime: 60 * 1000,
  });

  return { items: query.data?.items ?? [], degraded: query.data?.degraded ?? false, isLoading: query.isLoading, refetch: () => void query.refetch() };
}
